import * as React from "react"
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area"

import { cn } from "@/helpers/classNames"

/**
 * Augments native scroll functionality for custom, cross-browser styling.
 * From the original implementation in shadcn/ui. the `ref` was moved to the Viewport component
 * to be able to trigger scrolling to the selected item in the list.
 */
function ScrollArea({
  children,
  className,
  ref,
  ...props
}: {
  ref?: React.Ref<React.ElementRef<typeof ScrollAreaPrimitive.Root>>
} & React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root>) {
  return (
    <ScrollAreaPrimitive.Root
      className={cn("relative overflow-hidden", className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        className="size-full rounded-[inherit]"
        ref={ref}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  )
}

function ScrollBar({
  className,
  orientation = "vertical",
  ref,
  ...props
}: {
  ref?: React.Ref<
    React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
  >
} & React.ComponentPropsWithoutRef<
  typeof ScrollAreaPrimitive.ScrollAreaScrollbar
>) {
  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      ref={ref}
      orientation={orientation}
      className={cn(
        "flex touch-none transition-colors select-none",
        orientation === "vertical" &&
          "h-full w-2.5 border-l border-l-transparent p-px",
        orientation === "horizontal" &&
          "h-2.5 flex-col border-t border-t-transparent p-px",
        className,
      )}
      {...props}
    >
      {/* eslint-disable-next-line better-tailwindcss/enforce-consistent-line-wrapping */}
      <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  )
}

export { ScrollArea }
